import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, GitBranch, Play, Clock, CheckCircle, XCircle, User, ArrowRight, AlertCircle, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200'
};

export default function FMSPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [flowcharts, setFlowcharts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [selectedFlowchart, setSelectedFlowchart] = useState('');
  
  // Step completion dialog state
  const [completeDialog, setCompleteDialog] = useState({ open: false, task: null, step: null });
  const [completionNotes, setCompletionNotes] = useState('');
  const [decisionResult, setDecisionResult] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFlowcharts = async () => {
    try {
      const response = await axios.get(`${API_URL}/fms/flowcharts`, { headers: getAuthHeader() });
      setFlowcharts(response.data);
    } catch (error) {
      toast.error('Failed to fetch flowcharts');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/fms/tasks`, { headers: getAuthHeader() });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  useEffect(() => {
    fetchFlowcharts();
    fetchTasks();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    try {
      // Filter to Office Staff only for FMS task assignment
      const response = await axios.get(`${API_URL}/employees?staff_type=Office Staff`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const getUserName = (userId) => {
    const foundUser = users.find(u => u.id === userId);
    return foundUser?.name || foundUser?.email || 'Unassigned';
  };

  const handleOpenCompleteDialog = (task, step) => {
    setCompleteDialog({ open: true, task, step });
    setCompletionNotes('');
    setDecisionResult('');
  };

  const handleCompleteStep = async () => {
    if (!completeDialog.task || !completeDialog.step) return;
    
    const { task, step } = completeDialog;
    const isDecision = step.step_type === 'decision';
    
    if (isDecision && !decisionResult) {
      toast.error('Please select Yes or No for the decision');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const updateData = {
        status: 'completed',
        notes: completionNotes || null
      };
      
      if (isDecision) {
        updateData.decision_result = decisionResult;
      }
      
      await axios.patch(
        `${API_URL}/fms/tasks/${task.id}/steps/${step.step_id}`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      toast.success(`Step "${step.title}" marked as complete`);
      setCompleteDialog({ open: false, task: null, step: null });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete step');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTask = async () => {
    try {
      await axios.post(`${API_URL}/fms/tasks`, { flowchart_id: selectedFlowchart }, { headers: getAuthHeader() });
      toast.success('FMS task created successfully');
      setOpenCreate(false);
      setSelectedFlowchart('');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleDeleteTask = async () => {
    if (!deleteDialog.item) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/fms/tasks/${deleteDialog.item.id}`, { headers: getAuthHeader() });
      toast.success('FMS task deleted successfully');
      setDeleteDialog({ open: false, type: null, item: null });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteFlowchart = async () => {
    if (!deleteDialog.item) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/fms/flowcharts/${deleteDialog.item.id}`, { headers: getAuthHeader() });
      toast.success('FMS flowchart deleted successfully');
      setDeleteDialog({ open: false, type: null, item: null });
      fetchFlowcharts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete flowchart');
    } finally {
      setIsDeleting(false);
    }
  };

  const calculateProgress = (task) => {
    const completedSteps = task.steps.filter(s => s.status === 'completed').length;
    return Math.round((completedSteps / task.steps.length) * 100);
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold mb-4">Flowcharts</h3>
          <div className="flex justify-between items-center mb-4">
            <p className="text-muted-foreground">Manage workflow templates and processes</p>
            {(user?.is_owner || user?.allowed_modules?.includes('fms')) && (
              <Button onClick={() => navigate('/fms/flowchart/new')} data-testid="create-flowchart-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Flowchart
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {flowcharts.map((flowchart, index) => (
              <motion.div
                key={flowchart.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  data-testid={`flowchart-${flowchart.id}`}
                  onClick={() => navigate(`/fms/flowchart/${flowchart.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <GitBranch className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{flowchart.name}</CardTitle>
                          <CardDescription className="text-sm">
                            {flowchart.steps.length} steps
                          </CardDescription>
                        </div>
                      </div>
                      {(user?.is_owner || user?.allowed_modules?.includes('fms')) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, type: 'flowchart', item: flowchart });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">{flowchart.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Active FMS Tasks</h3>
            {(user?.is_owner || user?.allowed_modules?.includes('fms')) && flowcharts.length > 0 && (
              <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogTrigger asChild>
                  <Button data-testid="start-fms-task-btn">
                    <Play className="w-4 h-4 mr-2" />
                    Start New Task
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Start FMS Task</DialogTitle>
                    <DialogDescription>Select a flowchart to create a new task instance</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Select Flowchart</Label>
                      <Select value={selectedFlowchart} onValueChange={setSelectedFlowchart}>
                        <SelectTrigger data-testid="fms-flowchart-select">
                          <SelectValue placeholder="Choose a flowchart" />
                        </SelectTrigger>
                        <SelectContent>
                          {flowcharts.map((fc) => (
                            <SelectItem key={fc.id} value={fc.id}>
                              {fc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateTask} disabled={!selectedFlowchart} data-testid="fms-task-submit">
                      Create Task
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            {tasks.map((task, index) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="hover:shadow-lg transition-shadow" data-testid={`fms-task-${task.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{task.flowchart_name}</CardTitle>
                        <CardDescription>
                          Created {format(new Date(task.created_at), 'dd/MM/yyyy HH:mm')}
                          {task.linked_entity_number && (
                            <span className="ml-2 text-primary font-medium">
                              • Linked to: {task.linked_entity_number}
                            </span>
                          )}
                          {task.trigger_source && (
                            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                              Auto-triggered
                            </span>
                          )}
                        </CardDescription>
                      </div>
                      <Badge className={statusColors[task.overall_status]}>
                        {task.overall_status.replace('_', ' ')}
                      </Badge>
                      {(user?.is_owner || user?.allowed_modules?.includes('fms')) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteDialog({ open: true, type: 'task', item: task })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{calculateProgress(task)}%</span>
                        </div>
                        <div className="w-full bg-secondary rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${calculateProgress(task)}%` }}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        {task.steps.map((step, idx) => {
                          const isCurrentStep = idx === task.current_step_index && task.overall_status !== 'completed';
                          const isAssignedToMe = step.assigned_to === user?.id;
                          const canComplete = isCurrentStep && step.status === 'in_progress';
                          
                          return (
                          <div
                            key={step.step_id}
                            data-testid={`fms-step-${step.step_id}`}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isCurrentStep
                                ? 'border-primary bg-accent'
                                : step.status === 'completed'
                                ? 'border-green-200 bg-green-50/50'
                                : 'border-border'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                  step.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : step.status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {step.status === 'completed' ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : (
                                  idx + 1
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  {step.title}
                                  {step.step_type === 'decision' && (
                                    <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Decision</span>
                                  )}
                                </p>
                                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    TAT: {step.tat_hours}h
                                  </span>
                                  {step.assigned_to && (
                                    <span className="flex items-center gap-1">
                                      <User className="w-3 h-3" />
                                      {getUserName(step.assigned_to)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {canComplete && (
                                <Button
                                  size="sm"
                                  onClick={() => handleOpenCompleteDialog(task, step)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  {step.step_type === 'decision' ? 'Make Decision' : 'Mark Complete'}
                                </Button>
                              )}
                              <Badge variant="outline" className={statusColors[step.status]}>
                                {step.status.replace('_', ' ')}
                              </Badge>
                            </div>
                          </div>
                        )})}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {tasks.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No active FMS tasks</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Step Completion Dialog */}
      <Dialog open={completeDialog.open} onOpenChange={(open) => !open && setCompleteDialog({ open: false, task: null, step: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {completeDialog.step?.step_type === 'decision' ? 'Make Decision' : 'Complete Step'}
            </DialogTitle>
            <DialogDescription>
              {completeDialog.step?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Decision Options */}
            {completeDialog.step?.step_type === 'decision' && (
              <div className="space-y-3">
                <Label className="text-base font-medium">Decision Result *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    variant={decisionResult === 'yes' ? 'default' : 'outline'}
                    className={`h-16 ${decisionResult === 'yes' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                    onClick={() => setDecisionResult('yes')}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    YES
                  </Button>
                  <Button
                    type="button"
                    variant={decisionResult === 'no' ? 'default' : 'outline'}
                    className={`h-16 ${decisionResult === 'no' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                    onClick={() => setDecisionResult('no')}
                  >
                    <XCircle className="w-5 h-5 mr-2" />
                    NO
                  </Button>
                </div>
                {decisionResult && (
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    <p className="font-medium flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      Next: {decisionResult === 'yes' 
                        ? (completeDialog.step?.yes_branch?.action === 'end_workflow' 
                            ? 'End Workflow'
                            : completeDialog.step?.yes_branch?.action === 'goto_step'
                              ? 'Go to specified step'
                              : 'Continue to next step')
                        : (completeDialog.step?.no_branch?.action === 'end_workflow' 
                            ? 'End Workflow'
                            : completeDialog.step?.no_branch?.action === 'goto_step'
                              ? 'Go to specified step'
                              : 'Continue to next step')}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Completion Notes */}
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Add any notes about this step completion..."
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog({ open: false, task: null, step: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteStep} 
              disabled={isSubmitting || (completeDialog.step?.step_type === 'decision' && !decisionResult)}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? 'Completing...' : 'Confirm Completion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, item: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete {deleteDialog.type === 'task' ? 'FMS Task' : 'Flowchart'}
            </DialogTitle>
            <DialogDescription>
              {deleteDialog.type === 'task' 
                ? `Are you sure you want to delete the task "${deleteDialog.item?.flowchart_name}"? This action cannot be undone.`
                : `Are you sure you want to delete the flowchart "${deleteDialog.item?.name}"? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, item: null })}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={deleteDialog.type === 'task' ? handleDeleteTask : handleDeleteFlowchart}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
